import { Info, Atom } from '@phosphor-icons/react';
import { DataPanel } from './DataPanel';
import { hormoneRanges, hormoneEducation } from './constants/hormoneData';
import { getHormoneStatus } from './utils/hormoneUtils';

export const HormonalBalancePanel = ({ hormones, onSelectHormone }) => {
  if (!hormones) return null;
  
  return (
    <DataPanel title="Hormonal Balance" icon={Atom} color="#7C3AED">
      <div className="space-y-3">
        {Object.entries(hormoneRanges).map(([key, range]) => {
          const value = hormones[key];
          if (!value) return null;
          
          const status = getHormoneStatus(key, value);
          // Calculate percentage position within an extended range for visualization
          const minDisplay = range.min * 0.5;
          const maxDisplay = range.max * 1.5;
          const percentage = Math.min(100, Math.max(0, ((value - minDisplay) / (maxDisplay - minDisplay)) * 100));
          const normalStart = ((range.min - minDisplay) / (maxDisplay - minDisplay)) * 100;
          const normalEnd = ((range.max - minDisplay) / (maxDisplay - minDisplay)) * 100;
          
          // Map key to education key
          const eduKey = key === 'estradiol' ? 'estradiol' : key;
          
          return (
            <div key={key} className="space-y-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <span className="text-xs font-medium text-[#334155]">{range.name}</span>
                  {hormoneEducation[eduKey] && (
                    <button 
                      onClick={() => onSelectHormone(eduKey)}
                      className="w-4 h-4 rounded-full bg-[#E2E8F0] hover:bg-[#CBD5E1] flex items-center justify-center transition-colors"
                      title="Learn more"
                    >
                      <Info className="w-2.5 h-2.5 text-[#64748B]" weight="bold" />
                    </button>
                  )}
                </div>
                <span className="text-xs text-[#64748B]">
                  {value} {range.unit}
                  <span 
                    className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium"
                    style={{ 
                      backgroundColor: status.status === 'normal' ? '#DCFCE7' : status.status === 'low' ? '#FEF3C7' : '#FEE2E2',
                      color: status.status === 'normal' ? '#166534' : status.status === 'low' ? '#92400E' : '#991B1B'
                    }}
                  >
                    {status.status === 'normal' ? 'Normal' : status.status === 'low' ? 'Low' : 'High'}
                  </span>
                </span>
              </div>
              <div className="relative h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                {/* Normal range indicator */}
                <div 
                  className="absolute h-full bg-[#BBF7D0] opacity-60"
                  style={{ left: `${normalStart}%`, width: `${normalEnd - normalStart}%` }}
                />
                {/* Current value marker */}
                <div 
                  className="absolute top-0 h-full w-1.5 rounded-full shadow-sm"
                  style={{ 
                    left: `${Math.min(98, Math.max(0, percentage))}%`,
                    backgroundColor: status.color,
                    transform: 'translateX(-50%)'
                  }}
                />
              </div>
              <div className="flex justify-between text-[9px] text-[#94A3B8]">
                <span>Low</span>
                <span className="text-[#10B981]">Normal: {range.min}-{range.max}</span>
                <span>High</span>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-[#64748B] text-center mt-4 pt-3 border-t border-[#E2E8F0]">
        Click <Info className="w-3 h-3 inline" /> to learn about each hormone
      </p>
    </DataPanel>
  );
};
